<template>
  <!-- Vue Loading Screen -->
  <div v-if="!loaded" class="flex items-center justify-center h-screen bg-white">
    <div class="flex flex-col items-center">
      <img src="@/assets/LoginLogo.png" alt="Loading..." class="h-64 mb-4" />
      <div v-if="!showReloadButton"
        class="w-8 h-8 border-3 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-6"></div>

      <!-- Reload section that appears after 30 seconds -->
      <div v-if="showReloadButton" class="text-center max-w-xs">
        <p class="text-gray-500 text-sm mb-4 leading-relaxed">
          Taking longer than expected to load...
        </p>
        <AppButton :title="reloading ? 'Reloading...' : 'Reload App'" :is-loading="reloading"
          @click="clearCacheAndReload" />
      </div>
    </div>
  </div>

  <!-- App Requirement Screen -->
  <div v-else-if="loaded && !(isStandalone) && Config.requireApp && !router.currentRoute.value.meta.allowBrowser">
    <div class="w-full content-centered">
      <AppEventContainer>
        <div class="w-full md:w-auto flex justify-center">
          <img style="max-width: 300PX" class="p-3 mb-3" src="@/assets/LoginLogo.png" alt="App login logo" />
        </div>
        <p class="text-2xl text-center my-3">Download the app to continue.</p>
        <MobileLinks :show-android="!isIOS" :show-ios="!isAndroid"></MobileLinks>
        <ReloadPrompt />
      </AppEventContainer>
    </div>
  </div>

  <!-- Main App -->
  <div v-else>
    <div id="outer-main" class="flex flex-col h-screen">
      <AppNotification />
      <TopNavigation v-show="showTopNav" class="print:hidden"></TopNavigation>
      <main id="main" role="main" class="flex-grow bg-white">
        <AppMessenger />
        <section v-if="shouldPromptReview && !router.currentRoute.value.meta.allowBrowser && loaded"
          class="mt-1 text-yellow-500 hidden md:block">
          <AppReview :storeUrl="isAndroid ? Config.googlePlayConfig.appReviewUrl : Config.iosConfig.appReviewUrl" />
        </section>
        <div id="main-wrapper" :class="{ 'no-top-nav': !showTopNav }">
          <div :class="{
            'h-screen': router.currentRoute.value.meta.hideNav,
            'pt-0': router.currentRoute.value.meta.hideNav,
          }" class="bg-white">
            <Suspense>              
              <div>
                <div v-if="isOffline" data-testid="OffineBanner" class="bg-caution font-semibold text-xl text-center">
                  Offline
                </div>
                <div id="router-content" class="m-4">
                  <router-view />
                </div>
              </div>
            </Suspense>
          </div>
        </div>
      </main>
      <div id="recaptcha-wrapper">
        <div id="recaptcha-container"></div>
      </div>
      <BottomNavigation class="block print:hidden" v-show="!router.currentRoute.value.meta.hideNav" />
      <ReloadPrompt />
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, nextTick, provide, watch, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserProfileStore } from "@/store/UserProfile";
import { useEventParticipantStore } from "@/store/EventParticipant";
import { useHomeStore } from "@/store/Home";
import { useAppStore } from "@/store/App";
import { getValue, RemoteConfig } from "firebase/remote-config";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import BottomNavigation from "@/components/BottomNavigation.vue";
import TopNavigation from "@/components/TopNavigation.vue";
import AppMessenger from "./components/AppMessenger.vue";
import MobileLinks from "@/components/MobileLinks.vue";
import AppEventContainer from "@/components/AppEventContainer.vue";
import AppReview from "@/components/AppReview.vue";
import ReloadPrompt from "./components/ReloadPrompt.vue";
import "bottom-navigation-vue/dist/style.css";
import { storeToRefs } from "pinia";
import Config from "@/config/Config";
import useDeviceDetection from "@/modules/useDeviceDetection";
import { notificationSymbol, useNotification } from '@/composables/useNotification';
import AppNotification from '@/components/AppNotification.vue';
import useUI from "@/modules/useUI";
import useEvent from "./modules/useEvent";
import PokerRunEventDataService from "./services/PokerRunEventDataService";
import SystemDataService from "./services/SystemDataService";
import AppButton from "./components/AppButton.vue";
const { adjustMainWrapperPosition } = useUI();

const remoteConfig = inject("remoteConfig") as RemoteConfig;
const isOffline = ref(false);
const loaded = ref(false);
const showReloadButton = ref(false);
const showRouterReloadButton = ref(false);
const reloading = ref(false);
provide("isOffline", isOffline);
const route = useRoute();
const router = useRouter();
const auth = getAuth();
const { setIsAuthenticated, setLoginProfile, setVerifyEmail } = useUserProfileStore();
const { isEventActive, isPastEvent } = useEvent();
const eventParticipantStore = useEventParticipantStore();
const appStore = useAppStore();
const { setStandalone } = useAppStore();
const { standalone } = storeToRefs(useAppStore());
const homeStore = useHomeStore();
const [Platform, Installed] = useDeviceDetection();

const { isUserAuthenticated, profile, shouldPromptReview } = storeToRefs(useUserProfileStore());
const mapScript = document.createElement("script");
// Add TypeScript declaration for window.initGoogleMapsCallback
declare global {
  interface Window {
    initGoogleMapsCallback: () => void;
    google: any;
  }
}

// Include the marker and places libraries, the map ID, and the 'v=beta' parameter for the latest features
// The 'v=beta' parameter is REQUIRED for PlaceAutocompleteElement to be available
const mapUrl = `https://maps.googleapis.com/maps/api/js?key=${Config.googleMapsConfig.apiKey}&libraries=places,marker&v=beta&map_ids=${Config.googleMapsConfig.mapId}&loading=async&callback=initGoogleMapsCallback`;

// Define a global callback function that will be called when the Google Maps API is loaded
window.initGoogleMapsCallback = function () {
  console.log('Google Maps API loaded successfully with version beta');
  // Check if the PlaceAutocompleteElement is available
  if (window.google && window.google.maps && window.google.maps.places && window.google.maps.places.PlaceAutocompleteElement) {
    console.log('PlaceAutocompleteElement is available');
  } else {
    console.warn('PlaceAutocompleteElement is NOT available, will fall back to legacy Autocomplete');
  }
};
mapScript.setAttribute("src", mapUrl);
mapScript.setAttribute("loading", "async");
document.head.appendChild(mapScript);
// const isStandalone = ref(false);
const isAndroid = computed(
  () => Platform === "android"
);

const isIOS = computed(
  () => Platform === "ios"
);

const enableHelpScout = computed(() => {
  return getValue(remoteConfig, "EnableHelpScout")._value === "true";
});


(window.navigator.userAgent.includes("SamsungBrowser") && !window.locationbar.visible)

const isStandalone = computed(() =>
  Installed.value ||
  ((router.currentRoute.value.query.standalone ?? "false") === "true") ||
  standalone.value
);

const showTopNav = computed(() => {
  return !router.currentRoute.value.meta.hideTopNav;
});

watch(isStandalone, (value) => {
  setStandalone(value);
});

onAuthStateChanged(auth, async (user) => {
  if (user) {
    setIsAuthenticated(true);
    await setLoginProfile(user)

    if (route.meta.doNotRedirect) {
      return;
    }

    const items = await PokerRunEventDataService.getUserPokerRuns(user.uid);
    const activeEvents = items.filter(x => isEventActive(x));
    if (activeEvents !== null && activeEvents.length === 1) {
      router.push({ name: "RegisteredEvent", params: { id: activeEvents[0].id } });
    }
    else if (activeEvents !== null && activeEvents.length > 1) {
      router.push({ name: "YourEvents" });
    }
  } else {
    setIsAuthenticated(false);
    setVerifyEmail(false);
    await setLoginProfile(null);
  }
});

const handleConnection = () => {
  if (isOffline.value && navigator.onLine) {
    setTimeout(() => {
      if (!isOffline.value) {
        window.location.reload();
      }
    }, 15000);
  }

  isOffline.value = !navigator.onLine;
  eventParticipantStore.setIsOffline(isOffline.value);
  homeStore.setIsOffline(isOffline.value, profile.value.id);
  appStore.setIsOffline(isOffline.value);
};


isOffline.value = !navigator.onLine;
eventParticipantStore.setIsOffline(isOffline.value);
homeStore.setIsOffline(isOffline.value, profile.value.id);
appStore.setIsOffline(isOffline.value);
window.addEventListener("online", handleConnection);
window.addEventListener("offline", handleConnection);

// Cache clearing function
const clearCacheAndReload = async () => {
  reloading.value = true;

  try {
    // 1. Clear all Cache API caches (used by service workers)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => {
          console.log('Deleting cache:', cacheName);
          return caches.delete(cacheName);
        })
      );
    }

    // 2. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration) => {
          console.log('Unregistering service worker:', registration.scope);
          return registration.unregister();
        })
      );
    }

    // 3. Clear other browser storage
    if ('localStorage' in window) {
      localStorage.clear();
    }
    if ('sessionStorage' in window) {
      sessionStorage.clear();
    }
  } catch (error) {
    console.error('Cache clearing failed:', error);
  } finally {
    // Force reload with cache bypass regardless of clearing success
    window.location.reload();
  }
};

onMounted(() => nextTick(() => {
  // Hide the initial HTML loading screen
  const initialLoading = document.getElementById('initial-loading');
  if (initialLoading) {
    initialLoading.style.display = 'none';
  }

  // Show reload button after 30 seconds if app hasn't loaded
  const reloadTimer = setTimeout(() => {
    if (!loaded.value) {
      showReloadButton.value = true;
    }
  }, 20000);

  // Simple approach: just wait for the router to be ready and set loaded to true
  // The circular dependency issue: we can't check for DOM elements that only exist when loaded=true
  const stopWatcher = watch(
    () => router.currentRoute.value,
    (newRoute) => {
      if (newRoute.name && !loaded.value) {
        console.log('Route resolved:', newRoute.name);
        // Wait for next tick to ensure Vue has processed the route change
        nextTick(() => {
          // Give a small delay to ensure any async route components are loaded
          setTimeout(() => {
            console.log('Setting loaded to true after route resolution');
            loaded.value = true;
            clearTimeout(reloadTimer);
            stopWatcher(); // Stop watching after first load
            nextTick(() => {
              setTimeout(() => {
                adjustMainWrapperPosition();
              }, 100);

            });
          }, 200); // Small delay to ensure route component is ready
        });
      }
    },
    { immediate: true }
  );
}));

// Add route watcher with delayed adjustMainWrapperPosition
watch(
  () => route.path,
  () => {

    nextTick(() => {
      setTimeout(() => {
        adjustMainWrapperPosition();
      }, 100);
    });
  }
);

// Get the global instance and provide it
const notificationService = useNotification();
provide(notificationSymbol, notificationService);

</script>

<style>
body #app #outer-main {
  overflow: hidden;
}

#main-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  height: calc(100vh - 8rem);
  /* Remove the fixed padding-top */
}

#main-wrapper.no-top-nav {
  height: calc(100vh);
}

#main-wrapper>div {
  margin-left: auto;
  margin-right: auto;
}

/* Ensure the notification doesn't cause layout shifts */
.notification-aware {
  transition: transform 0.3s ease;
}

@media print {
  #main {
    max-height: 100%;
  }
}
</style>
